import { Component, computed, inject, input } from '@angular/core';

import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import { FormInstanceDetailedDto, FormInstancesClient, FormSchedulesClient, UnityScheduleType } from '@unity/clients';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { CollapseStateService } from '@unity/services/collapse-state.service';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { GridFormResponseSharedComponent } from '@unity/shared/grid-form-response-shared/grid-form-response-shared.component';
import { ListFormInstanceSharedComponent } from '@unity/shared/list-form-instance-shared/list-form-instance-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { pluralize } from '@unity/utils/string-transform';
@Component({
  selector: 'unity-list-form-instance-page',
  imports: [
    ListFormInstanceSharedComponent,
    KENDO_LAYOUT,
    KENDO_BUTTONS,
    PageContainerComponent,
    ActionSharedComponent,
    GridFormResponseSharedComponent,
  ],
  templateUrl: './list-form-instance-page.component.html',
  providers: [CollapseStateService],
  styleUrl: './list-form-instance-page.component.scss',
})
export class ListFormInstancePageComponent extends AbstractGridComponent<FormInstanceDetailedDto> {
  private readonly formInstancesClient = inject(FormInstancesClient);
  private readonly formSchedulesClient = inject(FormSchedulesClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $formScheduleId = input.required({ alias: 'formScheduleId', transform: (value: string) => parseInt(value) });
  public readonly $highlightedRecordId = input(undefined, {
    alias: 'highlightedRecordId',
    transform: (value: string | undefined) => (value === undefined ? undefined : parseInt(value)),
  });

  protected readonly $agreementName = computed(() =>
    this.formScheduleResource.hasValue() ? this.formScheduleResource.value().agreement.name : undefined
  );

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreement = this.$agreementName();
    return `Agreement: ${agreement}`;
  });

  protected override resource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const formScheduleId = this.$formScheduleId();
      return agreementId && formScheduleId ? { agreementId, formScheduleId } : undefined;
    },
    stream: ({ params: { agreementId, formScheduleId } }) =>
      this.formInstancesClient.getAllForAgreementAsDetailed(agreementId, formScheduleId),
  });

  protected formScheduleResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const formScheduleId = this.$formScheduleId();
      return formScheduleId && agreementId ? { agreementId, formScheduleId } : undefined;
    },
    stream: ({ params: { agreementId, formScheduleId } }) =>
      this.formSchedulesClient.getOneForAgreementAsDetailed(agreementId, formScheduleId),
  });

  protected readonly $isAgreementLoading = this.formScheduleResource.isLoading;

  protected readonly $contentHeader = computed(() => {
    const formSchedule = this.formScheduleResource.value();
    if (!formSchedule) {
      return ``;
    }

    return pluralize(formSchedule.name);
  });

  protected readonly $useGrid = computed(() => {
    return this.formScheduleResource.value()?.type.enum_value === UnityScheduleType.Adhoc;
  });

  protected readonly $hasInstances = computed(() => this.resource.hasValue() && this.resource.value().count > 0);

  protected readonly $formInstances = computed(() => {
    if (!this.resource.hasValue()) {
      return;
    }
    return this.resource.value().data;
  });

  protected readonly $createFormResponse = computed(() => {
    const formScheduleResource = this.formScheduleResource.value();
    if (formScheduleResource == null || formScheduleResource.type.enum_value !== UnityScheduleType.Adhoc) {
      return;
    }

    return QuickActions.CreateFormForFormSchedule(
      formScheduleResource.agreement.id,
      formScheduleResource.id,
      pluralize(formScheduleResource.name)
    );
  });

  protected readonly $overdueInstances = computed(() => this.$formInstances()?.filter(fi => fi.is_overdue === true));
  protected readonly $openInstances = computed(() => this.$formInstances()?.filter(fi => fi.is_untouched === true && fi.is_open));
  protected readonly $draftInstances = computed(() => this.$formInstances()?.filter(fi => fi.is_draft === true));
  protected readonly $instancesNeedingChanges = computed(() => this.$formInstances()?.filter(fi => fi.is_changes_requested === true));
  protected readonly $instancesPendingApproval = computed(() => this.$formInstances()?.filter(fi => fi.is_pending_approval === true));
  protected readonly $instancesPendingDirectorApproval = computed(() =>
    this.$formInstances()?.filter(fi => fi.is_pending_director_approval === true)
  );
  protected readonly $completedInstances = computed(() => this.$formInstances()?.filter(fi => fi.is_completed === true));
  protected readonly $rejectedInstances = computed(() => this.$formInstances()?.filter(fi => fi.is_rejected === true));

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
